import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  IconButton,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import CloseIcon from "@mui/icons-material/Close";
import WarningIcon from "@mui/icons-material/Warning";
import axios from "axios";
import { toast } from "react-toastify";
import { formatDateForDisplay } from "../../utils/dateUtils";
import AppLayout from "../../components/AppLayout";
import API_BASE_URL from "../../config/api";

const API_URL = `${API_BASE_URL}/api`;

export default function TaskDetailsPage() {
  const { date } = useParams();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [unassignDialogOpen, setUnassignDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [eligibleStaff, setEligibleStaff] = useState([]);
  const [assignments, setAssignments] = useState([
    { staffId: "", amount: "", description: "" }
  ]);
  const [assigning, setAssigning] = useState(false);

  // Get year and month from URL query params for back navigation
  const searchParams = new URLSearchParams(window.location.search);
  const calendarYear = searchParams.get('year');
  const calendarMonth = searchParams.get('month');

  useEffect(() => {
    fetchTasks();
    fetchEligibleStaff();

    // Auto-refresh every hour to update priority calculations
    const refreshInterval = setInterval(() => {
      fetchTasks();
    }, 60 * 60 * 1000); // 1 hour

    return () => clearInterval(refreshInterval);
  }, [date]);

  const fetchTasks = async () => {
    try {
      // console.log("🔄 [fetchTasks] Starting fetch for date:", date);
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}/tasks/date/${date}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // console.log("🔄 [fetchTasks] Response received:", response.data);

      if (response.data.success) {
        // Group tasks by bill
        const bills = response.data.bills || [];
        // console.log("🔄 [fetchTasks] Setting tasks state with", bills.length, "bills");
        // console.log("🔄 [fetchTasks] Sample task data:", bills[0]?.tasks[0] ? {
        //   taskId: bills[0].tasks[0].TaskId,
        //   assignedStaffNames: bills[0].tasks[0].AssignedStaffNames,
        //   staffCompletionDetails: bills[0].tasks[0].StaffCompletionDetails
        // } : 'No tasks');
        
        // Force a new array reference to ensure React detects the change
        setTasks([...bills]);
      }
    } catch (error) {
      console.error("❌ [fetchTasks] Error fetching tasks:", error);
      setError("Failed to load tasks");
    } finally {
      setLoading(false);
    }
  };

  const fetchEligibleStaff = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}/tasks/eligible-staff`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success) {
        setEligibleStaff(response.data.staff || []);
      }
    } catch (error) {
      console.error("Error fetching eligible staff:", error);
    }
  };

  const handleOpenAssignDialog = async (task) => {
    console.log("🔵 Opening assign dialog for task:", task.TaskId);
    setSelectedTask(task);
    
    // Fetch existing assignments for this task
    try {
      const token = localStorage.getItem("token");
      console.log("🔵 Fetching assignments from:", `${API_URL}/tasks/${task.TaskId}/assignments`);
      const response = await axios.get(`${API_URL}/tasks/${task.TaskId}/assignments`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      console.log("🔵 Assignments response:", response.data);

      if (response.data.success && response.data.assignments.length > 0) {
        // Pre-populate with existing assignments
        const existingAssignments = response.data.assignments.map(assignment => ({
          staffId: assignment.StaffId.toString(),
          amount: assignment.PaymentAmount?.toString() || "",
          description: assignment.AssignmentNotes || ""
        }));
        console.log("🔵 Pre-populating with assignments:", existingAssignments);
        setAssignments(existingAssignments);
      } else {
        // No existing assignments, start with empty form
        console.log("🔵 No existing assignments, starting with empty form");
        setAssignments([{ staffId: "", amount: "", description: "" }]);
      }
    } catch (error) {
      console.error("❌ Error fetching task assignments:", error);
      // On error, start with empty form
      setAssignments([{ staffId: "", amount: "", description: "" }]);
    }
    
    setAssignDialogOpen(true);
  };

  const handleCloseAssignDialog = () => {
    setAssignDialogOpen(false);
    setSelectedTask(null);
    setAssignments([{ staffId: "", amount: "", description: "" }]);
  };

  const handleOpenUnassignDialog = (task) => {
    setSelectedTask(task);
    setUnassignDialogOpen(true);
  };

  const handleCloseUnassignDialog = () => {
    setUnassignDialogOpen(false);
    setSelectedTask(null);
  };

  const handleAddEmployee = () => {
    setAssignments([...assignments, { staffId: "", amount: "", description: "" }]);
  };

  const handleRemoveEmployee = (index) => {
    const newAssignments = assignments.filter((_, i) => i !== index);
    // If removing all assignments, keep one empty slot
    if (newAssignments.length === 0) {
      setAssignments([{ staffId: "", amount: "", description: "" }]);
    } else {
      setAssignments(newAssignments);
    }
  };

  const handleAssignmentChange = (index, field, value) => {
    const newAssignments = [...assignments];
    newAssignments[index][field] = value;
    setAssignments(newAssignments);
  };

  // Unassign with proper confirmation dialog
  const handleUnassignTask = async () => {
    try {
      setAssigning(true);
      const token = localStorage.getItem("token");
      
      await axios.post(`${API_URL}/tasks/unassign-all`, {
        taskId: selectedTask.TaskId,
        paymentAction: 'remove', // Always remove payment for simplicity
        reason: 'Unassigned by admin'
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      toast.success("All staff unassigned from task successfully");
      
      // Close dialogs and refresh
      handleCloseUnassignDialog();
      
      setTimeout(() => {
        fetchTasks();
      }, 500);
      
    } catch (error) {
      console.error("Error unassigning staff:", error);
      toast.error("Failed to unassign staff");
    } finally {
      setAssigning(false);
    }
  };

  const handleAssignTask = async () => {
    // Validate all assignments
    const validAssignments = assignments.filter(a => a.staffId && a.amount);
    
    // If no valid assignments, user wants to unassign everyone
    if (validAssignments.length === 0) {
      // Check if all fields are empty (user wants to unassign)
      const allEmpty = assignments.every(a => !a.staffId && !a.amount && !a.description);
      
      if (allEmpty) {
        // Open unassign dialog instead of simple confirmation
        handleCloseAssignDialog();
        handleOpenUnassignDialog(selectedTask);
        return;
      } else {
        toast.error("Please select at least one staff member and enter an amount");
        return;
      }
    }

    // Check for duplicate staff assignments
    const staffIds = validAssignments.map(a => a.staffId);
    const uniqueStaffIds = new Set(staffIds);
    if (staffIds.length !== uniqueStaffIds.size) {
      toast.error("Cannot assign the same staff member multiple times");
      return;
    }

    try {
      setAssigning(true);
      const token = localStorage.getItem("token");
      
      console.log("🟢 Starting assignment process");
      console.log("🟢 Valid assignments:", validAssignments);
      console.log("🟢 Task ID:", selectedTask.TaskId);
      
      // ALWAYS replace existing assignments when dialog is submitted
      // This ensures removed employees are properly unassigned
      let successCount = 0;
      let errorMessages = [];
      
      for (let i = 0; i < validAssignments.length; i++) {
        const assignment = validAssignments[i];
        const payload = {
          taskId: selectedTask.TaskId,
          staffId: parseInt(assignment.staffId),
          assignmentNotes: assignment.description || "",
          paymentAmount: parseFloat(assignment.amount) || 0,
          // Replace existing on the FIRST assignment to clear old ones
          replaceExisting: i === 0
        };

        console.log(`🟢 Assignment ${i + 1}/${validAssignments.length}:`, payload);

        try {
          const response = await axios.post(`${API_URL}/tasks/assign`, payload, {
            headers: { Authorization: `Bearer ${token}` },
          });

          console.log(`✅ Assignment ${i + 1} response:`, response.data);

          if (response.data.success) {
            successCount++;
          }
        } catch (err) {
          console.error(`❌ Error assigning to staff ${assignment.staffId}:`, err);
          errorMessages.push(err.response?.data?.error || "Unknown error");
        }
      }

      console.log(`🟢 Assignment complete. Success: ${successCount}, Errors: ${errorMessages.length}`);

      if (successCount > 0) {
        if (errorMessages.length > 0) {
          toast.warning(`Task assigned to ${successCount} employee(s), but ${errorMessages.length} failed`);
        } else {
          toast.success(`Task assigned to ${successCount} employee(s) successfully`);
        }
        console.log("🟢 Closing dialog and refreshing tasks...");
        handleCloseAssignDialog();
        
        // Add a small delay to ensure database has been updated
        setTimeout(() => {
          console.log("🔄 Fetching updated tasks...");
          fetchTasks();
        }, 500);
      } else {
        toast.error("Failed to assign task to any employees");
      }
    } catch (error) {
      console.error("Error assigning task:", error);
      toast.error("Failed to assign task");
    } finally {
      setAssigning(false);
    }
  };

  const getPriorityColor = (task) => {
    const daysUntilDue = Math.ceil(
      (new Date(task.DueDate) - new Date()) / (1000 * 60 * 60 * 24)
    );

    if (daysUntilDue < 7) return "#F44336"; // Red - High Priority
    if (daysUntilDue <= 15) return "#CE93D8"; // Purple - Medium Priority
    return "#F57C00"; // Orange - Low Priority
  };

  const getPriorityLabel = (task) => {
    const daysUntilDue = Math.ceil(
      (new Date(task.DueDate) - new Date()) / (1000 * 60 * 60 * 24)
    );

    if (daysUntilDue < 7) return "High Priority";
    if (daysUntilDue <= 15) return "Medium Priority";
    return "Low Priority";
  };



  if (loading) {
    return (
      <AppLayout>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "80vh",
            bgcolor: "#1a1a1a",
          }}
        >
          <CircularProgress sx={{ color: "#C4A574" }} />
        </Box>
      </AppLayout>
    );
  }

  if (error) {
    return (
      <AppLayout>
        <Box sx={{ p: 3, bgcolor: "#1a1a1a", minHeight: "100vh" }}>
          <Alert 
            severity="error"
            sx={{
              bgcolor: "rgba(244, 67, 54, 0.15)",
              color: "#ff6b6b",
              "& .MuiAlert-icon": {
                color: "#ff6b6b"
              }
            }}
          >
            {error}
          </Alert>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => {
              if (calendarYear && calendarMonth) {
                navigate(`/admin/tasks?year=${calendarYear}&month=${calendarMonth}`);
              } else {
                navigate("/admin/tasks");
              }
            }}
            sx={{ 
              mt: 2,
              color: "#C4A574",
              "&:hover": {
                bgcolor: "rgba(196, 165, 116, 0.1)"
              }
            }}
          >
            Back to Calendar
          </Button>
        </Box>
      </AppLayout>
    );
  }

  // Calculate summary stats
  const summaryStats = tasks.reduce(
    (acc, bill) => {
      bill.tasks.forEach((task) => {
        acc.total++;
        
        // Count completed tasks (Status = "Completed")
        if (task.Status === "Completed") {
          acc.completed++;
        }
        
        // Count assigned tasks (tasks that have staff assigned but not completed)
        if (task.AssignedStaffNames && task.AssignedStaffNames.trim() !== "" && task.Status !== "Completed") {
          acc.assigned++;
        }
        
        // Count pending tasks (no staff assigned and not completed)
        if ((!task.AssignedStaffNames || task.AssignedStaffNames.trim() === "") && task.Status !== "Completed") {
          acc.pending++;
        }
        
        const daysUntilDue = Math.ceil(
          (new Date(task.DueDate) - new Date()) / (1000 * 60 * 60 * 24)
        );
        if (daysUntilDue < 7) acc.high++;
        else if (daysUntilDue <= 15) acc.medium++;
        else acc.low++;
      });
      return acc;
    },
    { total: 0, high: 0, medium: 0, low: 0, completed: 0, assigned: 0, pending: 0 }
  );

  return (
    <AppLayout>
      <Box sx={{ p: 3, maxWidth: 1400, margin: "0 auto", bgcolor: "#1a1a1a", minHeight: "100vh" }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => {
              // Navigate back to the calendar with the same year/month if available
              if (calendarYear && calendarMonth) {
                navigate(`/admin/tasks?year=${calendarYear}&month=${calendarMonth}`);
              } else {
                navigate("/admin/tasks");
              }
            }}
            sx={{ 
              mb: 2, 
              color: "#C4A574",
              "&:hover": {
                bgcolor: "rgba(196, 165, 116, 0.1)"
              }
            }}
          >
            Back to Calendar
          </Button>
          <Typography variant="h4" sx={{ fontWeight: 700, color: "#C4A574" }}>
            Tasks for {formatDateForDisplay(date)}
          </Typography>
        </Box>

        {/* Summary Stats */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={4} md={2}>
            <Card sx={{ bgcolor: "#3d3d3f", boxShadow: 2 }}>
              <CardContent sx={{ textAlign: "center", py: { xs: 1, sm: 2 } }}>
                <Typography 
                  variant="h5" 
                  sx={{ 
                    fontWeight: 700, 
                    color: "#C4A574",
                    fontSize: { xs: '1.2rem', sm: '1.5rem' }
                  }}
                >
                  {summaryStats.total}
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: "#d0cfcf",
                    fontSize: { xs: '0.8rem', sm: '0.875rem' }
                  }}
                >
                  Total Tasks
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Card sx={{ bgcolor: "#3a3f3a", boxShadow: 2 }}>
              <CardContent sx={{ textAlign: "center", py: { xs: 1, sm: 2 } }}>
                <Typography 
                  variant="h5" 
                  sx={{ 
                    fontWeight: 700, 
                    color: "#A5D6A7",
                    fontSize: { xs: '1.2rem', sm: '1.5rem' }
                  }}
                >
                  {summaryStats.completed}
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: "#d0cfcf",
                    fontSize: { xs: '0.8rem', sm: '0.875rem' }
                  }}
                >
                  Completed
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Card sx={{ bgcolor: "#1f3d3d", boxShadow: 2 }}>
              <CardContent sx={{ textAlign: "center", py: { xs: 1, sm: 2 } }}>
                <Typography 
                  variant="h5" 
                  sx={{ 
                    fontWeight: 700, 
                    color: "#4FC3F7",
                    fontSize: { xs: '1.2rem', sm: '1.5rem' }
                  }}
                >
                  {summaryStats.assigned}
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: "#d0cfcf",
                    fontSize: { xs: '0.8rem', sm: '0.875rem' }
                  }}
                >
                  Assigned
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Card sx={{ bgcolor: "#3d3d1f", boxShadow: 2 }}>
              <CardContent sx={{ textAlign: "center", py: { xs: 1, sm: 2 } }}>
                <Typography 
                  variant="h5" 
                  sx={{ 
                    fontWeight: 700, 
                    color: "#FFD54F",
                    fontSize: { xs: '1.2rem', sm: '1.5rem' }
                  }}
                >
                  {summaryStats.pending}
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: "#d0cfcf",
                    fontSize: { xs: '0.8rem', sm: '0.875rem' }
                  }}
                >
                  Pending
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={4} sm={4} md={2}>
            <Card sx={{ bgcolor: "#3d1f1f", boxShadow: 2 }}>
              <CardContent sx={{ textAlign: "center", py: { xs: 1, sm: 2 } }}>
                <Typography 
                  variant="h5" 
                  sx={{ 
                    fontWeight: 700, 
                    color: "#F44336",
                    fontSize: { xs: '1rem', sm: '1.25rem' }
                  }}
                >
                  {summaryStats.high}
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: "#d0cfcf",
                    fontSize: { xs: '0.7rem', sm: '0.875rem' }
                  }}
                >
                  High Priority
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={4} sm={4} md={2}>
            <Card sx={{ bgcolor: "#3d243d", boxShadow: 2 }}>
              <CardContent sx={{ textAlign: "center", py: { xs: 1, sm: 2 } }}>
                <Typography 
                  variant="h5" 
                  sx={{ 
                    fontWeight: 700, 
                    color: "#CE93D8",
                    fontSize: { xs: '1rem', sm: '1.25rem' }
                  }}
                >
                  {summaryStats.medium}
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: "#d0cfcf",
                    fontSize: { xs: '0.7rem', sm: '0.875rem' }
                  }}
                >
                  Medium Priority
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={4} sm={4} md={2}>
            <Card sx={{ bgcolor: "#3d331f", boxShadow: 2 }}>
              <CardContent sx={{ textAlign: "center", py: { xs: 1, sm: 2 } }}>
                <Typography 
                  variant="h5" 
                  sx={{ 
                    fontWeight: 700, 
                    color: "#F57C00",
                    fontSize: { xs: '1rem', sm: '1.25rem' }
                  }}
                >
                  {summaryStats.low}
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: "#d0cfcf",
                    fontSize: { xs: '0.7rem', sm: '0.875rem' }
                  }}
                >
                  Low Priority
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Priority Legend */}
        <Box
          sx={{
            display: "flex",
            gap: 2,
            mb: 3,
            flexWrap: "wrap",
            justifyContent: "center",
          }}
        >
          <Chip
            label="High Priority (< 7 days)"
            sx={{ bgcolor: "#F44336", color: "white", fontWeight: 600 }}
          />
          <Chip
            label="Medium Priority (7-15 days)"
            sx={{ bgcolor: "#CE93D8", color: "white", fontWeight: 600 }}
          />
          <Chip
            label="Low Priority (> 15 days)"
            sx={{ bgcolor: "#F57C00", color: "white", fontWeight: 600 }}
          />
        </Box>

      {/* Tasks by Bill */}
      {tasks.length === 0 ? (
        <Alert 
          severity="info"
          sx={{
            bgcolor: "rgba(33, 150, 243, 0.15)",
            color: "#64b5f6",
            "& .MuiAlert-icon": {
              color: "#64b5f6"
            }
          }}
        >
          No tasks found for this date
        </Alert>
      ) : (
        <Grid container spacing={3}>
          {tasks.map((bill) => (
            <Grid item xs={12} key={bill.billId}>
              <Card
                sx={{
                  borderRadius: 2,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
                  overflow: "hidden",
                  bgcolor: "#2a2a2a",
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  {/* Bill Header */}
                  <Box
                    sx={{
                      mb: 3,
                      pb: 2,
                      borderBottom: "2px solid rgba(196, 165, 116, 0.3)",
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 700, color: "#C4A574", mb: 0.5 }}
                    >
                      Bill #{bill.billNumber}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d0cfcf" }}>
                      {bill.customerName} • {bill.customerPhone}
                    </Typography>
                  </Box>

                  {/* Tasks for this Bill */}
                  <Grid container spacing={2}>
                    {bill.tasks.map((task) => {
                      const priorityColor = getPriorityColor(task);
                      
                      return (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={task.TaskId}>
                          <Card
                            sx={{
                              borderRadius: 2,
                              boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
                              overflow: "hidden",
                              transition: "transform 0.2s, boxShadow 0.2s",
                              "&:hover": {
                                transform: "translateY(-4px)",
                                boxShadow: "0 8px 20px rgba(0,0,0,0.35)",
                              },
                              bgcolor: "#1a1a1a",
                              border: `2px solid ${priorityColor}`,
                              height: "100%",
                            }}
                          >
                            <CardContent sx={{ p: 2 }}>
                              {/* Priority Badge - Above Task Type */}
                              <Box
                                sx={{
                                  bgcolor: priorityColor,
                                  px: 1.25,
                                  py: 0.5,
                                  borderRadius: 1,
                                  display: "inline-block",
                                  mb: 1.5,
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: "white",
                                    fontWeight: 700,
                                    fontSize: "0.7rem",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.5px",
                                  }}
                                >
                                  {getPriorityLabel(task)}
                                </Typography>
                              </Box>

                              <Typography
                                variant="subtitle1"
                                sx={{
                                  fontWeight: 700,
                                  color: priorityColor,
                                  mb: 1,
                                  fontSize: "1.1rem",
                                }}
                              >
                                {task.TaskType}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ 
                                  color: "#C4A574", 
                                  mb: 2,
                                  minHeight: "40px",
                                }}
                              >
                                {task.Description}
                              </Typography>
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: 0.75,
                                  mt: 2,
                                  pt: 2,
                                  borderTop: `1px solid ${priorityColor}33`,
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{ 
                                    color: "#d0cfcf",
                                    fontSize: "0.8rem",
                                  }}
                                >
                                  <strong>Product:</strong> {task.ProductName} ({task.ProductSize})
                                </Typography>
                                {task.ProductCategory === 'Customized' ? (
                                  <>
                                    <Typography
                                      variant="caption"
                                      sx={{ 
                                        color: "#d0cfcf",
                                        fontSize: "0.8rem",
                                      }}
                                    >
                                      <strong>Product Cost:</strong> ₹{(task.ProductUnitPrice * task.ProductQuantity).toFixed(2)}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      sx={{ 
                                        color: "#d0cfcf",
                                        fontSize: "0.8rem",
                                      }}
                                    >
                                      <strong>Customizations:</strong> ₹{parseFloat(task.CustomizationCost || 0).toFixed(2)}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      sx={{ 
                                        color: "#d0cfcf",
                                        fontSize: "0.8rem",
                                        fontWeight: 600,
                                      }}
                                    >
                                      <strong>Total Cost:</strong> ₹{((task.ProductUnitPrice * task.ProductQuantity) + parseFloat(task.CustomizationCost || 0)).toFixed(2)}
                                    </Typography>
                                  </>
                                ) : (
                                  <Typography
                                    variant="caption"
                                    sx={{ 
                                      color: "#d0cfcf",
                                      fontSize: "0.8rem",
                                    }}
                                  >
                                    <strong>Customization Cost:</strong> ₹{parseFloat(task.CustomizationCost || 0).toFixed(2)}
                                  </Typography>
                                )}
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                  <Typography
                                    variant="caption"
                                    sx={{ 
                                      color: "#d0cfcf",
                                      fontSize: "0.8rem",
                                      fontWeight: 600,
                                    }}
                                  >
                                    Status:
                                  </Typography>
                                  <Box
                                    sx={{
                                      px: 1,
                                      py: 0.25,
                                      borderRadius: 0.5,
                                      bgcolor: task.Status === "Completed" ? "#4CAF50" : 
                                               task.Status === "InProgress" ? "#FF9800" : 
                                               task.Status === "Assigned" ? "#2196F3" : "#9E9E9E",
                                      display: "inline-block",
                                    }}
                                  >
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color: "white",
                                        fontWeight: 700,
                                        fontSize: "0.7rem",
                                      }}
                                    >
                                      {task.Status}
                                    </Typography>
                                  </Box>
                                </Box>
                                {task.AssignedStaffNames ? (
                                  <Box sx={{ mt: 0.5 }}>
                                    <Box
                                      sx={{
                                        bgcolor: priorityColor,
                                        px: 1.5,
                                        py: 0.75,
                                        borderRadius: 1,
                                      }}
                                    >
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          color: "white",
                                          fontWeight: 600,
                                          fontSize: "0.75rem",
                                          mb: 0.5,
                                          display: "block",
                                        }}
                                      >
                                        ✓ Assigned to:
                                      </Typography>
                                      {task.StaffCompletionDetailsWithTimestamp ? (
                                        // Show individual staff completion status with timestamps
                                        task.StaffCompletionDetailsWithTimestamp.split('|').map((detail, idx) => {
                                          const parts = detail.split(':');
                                          const staffName = parts[0];
                                          const status = parts[1];
                                          // Join the remaining parts back together for the timestamp (in case timestamp contains colons)
                                          const completedAtStr = parts.slice(2).join(':');
                                          const isCompleted = status === 'Completed';
                                          
                                          let completionTimestamp = null;
                                          if (isCompleted && completedAtStr && completedAtStr.trim() !== '') {
                                            try {
                                              completionTimestamp = new Date(completedAtStr);
                                              // Check if the date is valid
                                              if (isNaN(completionTimestamp.getTime())) {
                                                completionTimestamp = null;
                                              }
                                            } catch (error) {
                                              console.error('Error parsing timestamp:', completedAtStr, error);
                                              completionTimestamp = null;
                                            }
                                          }
                                          
                                          return (
                                            <Box
                                              key={idx}
                                              sx={{
                                                display: "flex",
                                                flexDirection: "column",
                                                gap: 0.25,
                                                mt: idx > 0 ? 0.5 : 0,
                                              }}
                                            >
                                              <Box
                                                sx={{
                                                  display: "flex",
                                                  alignItems: "center",
                                                  gap: 0.5,
                                                }}
                                              >
                                                <Box
                                                  sx={{
                                                    width: 6,
                                                    height: 6,
                                                    borderRadius: "50%",
                                                    bgcolor: isCompleted ? "#4CAF50" : "#FFC107",
                                                    flexShrink: 0,
                                                  }}
                                                />
                                                <Typography
                                                  variant="caption"
                                                  sx={{
                                                    color: "white",
                                                    fontSize: "0.7rem",
                                                    opacity: isCompleted ? 1 : 0.85,
                                                  }}
                                                >
                                                  {staffName} {isCompleted ? "✓" : "⏳"}
                                                </Typography>
                                              </Box>
                                              {isCompleted && completionTimestamp && (
                                                <Typography
                                                  variant="caption"
                                                  sx={{
                                                    color: "white",
                                                    fontSize: "0.6rem",
                                                    opacity: 0.8,
                                                    ml: 1.25, // Align with the text above
                                                    fontStyle: "italic",
                                                  }}
                                                >
                                                  Completed: {completionTimestamp.toLocaleDateString('en-GB')} at {completionTimestamp.toLocaleTimeString('en-IN', { 
                                                    hour: '2-digit', 
                                                    minute: '2-digit',
                                                    timeZone: 'Asia/Kolkata'
                                                  })}
                                                </Typography>
                                              )}
                                            </Box>
                                          );
                                        })
                                      ) : task.StaffCompletionDetails ? (
                                        // Fallback to old format without timestamps
                                        task.StaffCompletionDetails.split('|').map((detail, idx) => {
                                          const [staffName, status] = detail.split(':');
                                          const isCompleted = status === 'Completed';
                                          return (
                                            <Box
                                              key={idx}
                                              sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 0.5,
                                                mt: idx > 0 ? 0.5 : 0,
                                              }}
                                            >
                                              <Box
                                                sx={{
                                                  width: 6,
                                                  height: 6,
                                                  borderRadius: "50%",
                                                  bgcolor: isCompleted ? "#4CAF50" : "#FFC107",
                                                  flexShrink: 0,
                                                }}
                                              />
                                              <Typography
                                                variant="caption"
                                                sx={{
                                                  color: "white",
                                                  fontSize: "0.7rem",
                                                  opacity: isCompleted ? 1 : 0.85,
                                                }}
                                              >
                                                {staffName} {isCompleted ? "✓" : "⏳"}
                                              </Typography>
                                            </Box>
                                          );
                                        })
                                      ) : (
                                        // Fallback to simple list
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            color: "white",
                                            fontSize: "0.7rem",
                                            display: "block",
                                          }}
                                        >
                                          {task.AssignedStaffNames}
                                        </Typography>
                                      )}
                                    </Box>
                                    <Box sx={{ display: "flex", gap: 0.5, mt: 0.5 }}>
                                      <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<PersonAddIcon />}
                                        onClick={() => handleOpenAssignDialog(task)}
                                        sx={{
                                          flex: 1,
                                          borderColor: priorityColor,
                                          color: priorityColor,
                                          fontSize: "0.6rem",
                                          py: 0.25,
                                          "&:hover": {
                                            borderColor: priorityColor,
                                            bgcolor: `${priorityColor}10`,
                                          },
                                        }}
                                      >
                                        Reassign
                                      </Button>
                                      <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<CloseIcon sx={{ fontSize: "0.7rem" }} />}
                                        onClick={() => handleOpenUnassignDialog(task)}
                                        sx={{
                                          flex: 1,
                                          borderColor: "#d32f2f",
                                          color: "#d32f2f",
                                          fontSize: "0.6rem",
                                          py: 0.25,
                                          "&:hover": {
                                            borderColor: "#d32f2f",
                                            bgcolor: "#d32f2f10",
                                          },
                                        }}
                                      >
                                        Unassign
                                      </Button>
                                    </Box>
                                  </Box>
                                ) : (
                                  <Button
                                    variant="contained"
                                    size="small"
                                    startIcon={<PersonAddIcon />}
                                    onClick={() => handleOpenAssignDialog(task)}
                                    sx={{
                                      mt: 1,
                                      bgcolor: priorityColor,
                                      "&:hover": {
                                        bgcolor: priorityColor,
                                        opacity: 0.9,
                                      },
                                      fontSize: "0.7rem",
                                      py: 0.5,
                                    }}
                                  >
                                    Assign Task
                                  </Button>
                                )}
                              </Box>
                            </CardContent>
                          </Card>
                        </Grid>
                      );
                    })}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Assignment Dialog */}
      <Dialog 
        open={assignDialogOpen} 
        onClose={handleCloseAssignDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { 
            borderRadius: 2,
            bgcolor: "#2a2a2a",
            color: "#fff"
          },
        }}
      >
        <DialogTitle 
          sx={{ 
            display: "flex", 
            justifyContent: "space-between", 
            alignItems: "center",
            bgcolor: "#8B7355",
            color: "#fff"
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {selectedTask?.AssignedStaffName ? "Reassign Task" : "Assign Task"}
          </Typography>
          <IconButton onClick={handleCloseAssignDialog} size="small" sx={{ color: "#fff" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ bgcolor: "#2a2a2a", mt: 2 }}>
          {selectedTask && (
            <Box sx={{ mb: 3, p: 2, bgcolor: "#1a1a1a", borderRadius: 1, border: "1px solid #444" }}>
              <Typography variant="subtitle2" sx={{ color: "#888" }}>
                Task Details
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5, color: "#C4A574" }}>
                {selectedTask.TaskType}: {selectedTask.Description}
              </Typography>
              <Typography variant="caption" sx={{ color: "#d0cfcf" }}>
                Product: {selectedTask.ProductName} ({selectedTask.ProductSize})
              </Typography>
            </Box>
          )}

          {assignments.map((assignment, index) => (
            <Box
              key={index}
              sx={{
                mb: 3,
                p: 2,
                border: "1px solid #444",
                borderRadius: 1,
                position: "relative",
                bgcolor: "#1a1a1a",
              }}
            >
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: "#C4A574" }}>
                Employee {index + 1}
              </Typography>

              {assignments.length > 1 && (
                <IconButton
                  size="small"
                  onClick={() => handleRemoveEmployee(index)}
                  sx={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    color: "#ff6b6b",
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel sx={{ color: "#C4A574", "&.Mui-focused": { color: "#C4A574" } }}>
                  Select Staff Member
                </InputLabel>
                <Select
                  value={assignment.staffId}
                  onChange={(e) => handleAssignmentChange(index, "staffId", e.target.value)}
                  label="Select Staff Member"
                  sx={{
                    backgroundColor: "#1a1a1a",
                    color: "#fff",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "rgba(196, 165, 116, 0.3)",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "rgba(196, 165, 116, 0.5)",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#C4A574",
                    },
                    "& .MuiSvgIcon-root": {
                      color: "#C4A574",
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        bgcolor: "#2a2a2a",
                        "& .MuiMenuItem-root": {
                          color: "#fff",
                          "&:hover": {
                            bgcolor: "rgba(196, 165, 116, 0.1)",
                          },
                          "&.Mui-selected": {
                            bgcolor: "rgba(196, 165, 116, 0.2)",
                            "&:hover": {
                              bgcolor: "rgba(196, 165, 116, 0.3)",
                            },
                          },
                        },
                      },
                    },
                  }}
                >
                  <MenuItem value="">
                    <em>Select a staff member</em>
                  </MenuItem>
                  {eligibleStaff.map((staff) => (
                    <MenuItem key={staff.StaffId} value={staff.StaffId}>
                      {staff.FullName} - {staff.Designation || "Staff"}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                type="number"
                label="Payment Amount (₹)"
                value={assignment.amount}
                onChange={(e) => handleAssignmentChange(index, "amount", e.target.value)}
                placeholder="Enter amount to be paid"
                sx={{ 
                  mb: 2,
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#1a1a1a",
                    color: "#fff",
                    "& fieldset": {
                      borderColor: "rgba(196, 165, 116, 0.3)",
                    },
                    "&:hover fieldset": {
                      borderColor: "rgba(196, 165, 116, 0.5)",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#C4A574",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: "#C4A574",
                    "&.Mui-focused": {
                      color: "#C4A574",
                    },
                  },
                  "& .MuiOutlinedInput-input::placeholder": {
                    color: "rgba(255, 255, 255, 0.5)",
                    opacity: 1,
                  },
                }}
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 1, color: "#C4A574" }}>₹</Typography>,
                }}
              />

              <TextField
                fullWidth
                multiline
                rows={2}
                label="Description/Notes (Optional)"
                value={assignment.description}
                onChange={(e) => handleAssignmentChange(index, "description", e.target.value)}
                placeholder="Add specific instructions for this employee..."
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#1a1a1a",
                    color: "#fff",
                    "& fieldset": {
                      borderColor: "rgba(196, 165, 116, 0.3)",
                    },
                    "&:hover fieldset": {
                      borderColor: "rgba(196, 165, 116, 0.5)",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#C4A574",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: "#C4A574",
                    "&.Mui-focused": {
                      color: "#C4A574",
                    },
                  },
                  "& .MuiOutlinedInput-input::placeholder": {
                    color: "rgba(255, 255, 255, 0.5)",
                    opacity: 1,
                  },
                }}
              />
            </Box>
          ))}

          <Button
            variant="outlined"
            fullWidth
            onClick={handleAddEmployee}
            sx={{
              mb: 2,
              borderColor: "#C4A574",
              color: "#C4A574",
              "&:hover": {
                borderColor: "#8B7355",
                bgcolor: "rgba(196, 165, 116, 0.1)",
              },
            }}
          >
            + Add Another Employee
          </Button>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, bgcolor: "#2a2a2a" }}>
          <Button 
            onClick={handleCloseAssignDialog} 
            disabled={assigning}
            sx={{ color: "#ccc" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAssignTask}
            disabled={assigning}
            sx={{ 
              bgcolor: "#C4A574",
              "&:hover": { bgcolor: "#8B7355" }
            }}
          >
            {assigning ? <CircularProgress size={24} sx={{ color: "#fff" }} /> : `Assign to ${assignments.filter(a => a.staffId).length} Employee(s)`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unassign Confirmation Dialog */}
      <Dialog
        open={unassignDialogOpen}
        onClose={handleCloseUnassignDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { 
            borderRadius: 2,
            bgcolor: "#2a2a2a",
            color: "#fff"
          },
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: '#d32f2f',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <WarningIcon />
          <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
            Unassign Task
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ mt: 2, bgcolor: "#2a2a2a" }}>
          <Alert 
            severity="warning" 
            sx={{ 
              mb: 2,
              bgcolor: "rgba(255, 152, 0, 0.15)",
              color: "#ff9800",
              "& .MuiAlert-icon": {
                color: "#ff9800"
              }
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              This will remove all staff assignments and reverse any payments
            </Typography>
          </Alert>

          <Typography variant="body1" sx={{ mb: 2, color: "#fff" }}>
            You are about to unassign all staff from the following task:
          </Typography>

          {selectedTask && (
            <Box
              sx={{
                bgcolor: '#1a1a1a',
                p: 2,
                borderRadius: 1,
                mb: 3,
                border: "1px solid #444"
              }}
            >
              <Typography variant="subtitle2" sx={{ color: "#888" }}>
                Task Type
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600, mb: 1, color: "#C4A574" }}>
                {selectedTask.TaskType}
              </Typography>

              <Typography variant="subtitle2" sx={{ color: "#888" }}>
                Description
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600, mb: 1, color: "#C4A574" }}>
                {selectedTask.Description}
              </Typography>

              <Typography variant="subtitle2" sx={{ color: "#888" }}>
                Product
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600, color: "#C4A574" }}>
                {selectedTask.ProductName} ({selectedTask.ProductSize})
              </Typography>

              {selectedTask.AssignedStaffNames && (
                <>
                  <Typography variant="subtitle2" sx={{ color: "#888", mt: 1 }}>
                    Currently Assigned To
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: "#ff9800" }}>
                    {selectedTask.AssignedStaffNames}
                  </Typography>
                </>
              )}
            </Box>
          )}

          <Typography variant="body2" sx={{ mb: 2, color: "#ccc" }}>
            <strong>Note:</strong> This action will remove the task from all assigned staff members and reverse any payments that have been made for this task.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ p: 2, bgcolor: '#2a2a2a' }}>
          <Button 
            onClick={handleCloseUnassignDialog} 
            disabled={assigning}
            sx={{ color: "#ccc" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUnassignTask}
            variant="contained"
            disabled={assigning}
            sx={{
              bgcolor: '#d32f2f',
              color: '#fff',
              fontWeight: 600,
              '&:hover': { bgcolor: '#b71c1c' },
            }}
          >
            {assigning ? <CircularProgress size={24} sx={{ color: "#fff" }} /> : 'Unassign All Staff'}
          </Button>
        </DialogActions>
      </Dialog>
      </Box>
    </AppLayout>
  );
}
